import {
  createDocument,
  createYjsStore,
  randomId,
  type DocumentHandle,
  type HouseholdRole,
  type RecipeStore,
  type RevisionBackend,
  type StoreContext,
} from '@platewell/core';
import type { SyncErrorPayload } from '@platewell/schema';
import { attachPersistence, type Persistence, type UpdateLogStore } from './persistence/update-log.js';
import { connectSync, type ConnectionStatus, type SyncConnection } from './transport/connection.js';

/**
 * Devices differ, so the active household is a *device-local* preference rather than
 * synced account state. A phone in the kitchen can sit in the family household while the
 * laptop is in a personal one, which is what people actually do.
 */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const ACTIVE_HOUSEHOLD_KEY = 'platewell.activeHouseholdId';
export const HOUSEHOLD_LIST_KEY = 'platewell.households';
/**
 * Households this device has completed at least one full sync of. An empty store means
 * "no recipes" only once the server has had its say; before that it means "not here yet".
 */
export const SYNCED_HOUSEHOLDS_KEY = 'platewell.syncedHouseholds';

/**
 * Connections are capped and rotated least-recently-used. A user in eight households
 * should not hold eight WebSockets and eight parsed documents open on a phone.
 */
export const MAX_CONCURRENT_HOUSEHOLDS = 3;

export interface HouseholdSession {
  readonly householdId: string;
  readonly role: HouseholdRole;
  readonly handle: DocumentHandle;
  readonly store: RecipeStore;
  readonly connection: SyncConnection;
  readonly persistence: Persistence;
  lastUsedAt: number;
}

export interface HouseholdManagerOptions {
  userId: string;
  /** Base URL of the Worker, e.g. "" in dev behind the Vite proxy. */
  apiBaseUrl: string;
  storage: KeyValueStorage;
  createUpdateLog: (documentId: string) => UpdateLogStore;
  /**
   * Overrides the sync WebSocket URL. React Native cannot attach an Authorization header to
   * a handshake, so the native app appends its session token here instead.
   */
  syncUrl?: (householdId: string) => string;
  revisions?: (householdId: string) => RevisionBackend;
  onStatusChange?: (householdId: string, status: ConnectionStatus) => void;
  onServerError?: (householdId: string, error: SyncErrorPayload) => void;
  /** A local storage read or write failed. Defaults to the console. */
  onPersistenceError?: (error: unknown) => void;
  webSocketImpl?: typeof WebSocket;
  newId?: () => string;
}

export type StatusListener = (householdId: string, status: ConnectionStatus) => void;

export type SyncedListener = (householdId: string) => void;

/**
 * Server-side rejections — quota, a lapsed subscription, a rate limit.
 *
 * These had nowhere to go before: `connectSync` accepted an `onServerError` and this class
 * never passed one, so no server error payload reached the app at all.
 */
export type ServerErrorListener = (householdId: string, error: SyncErrorPayload) => void;

/** Structurally the schema's HouseholdSummary; kept local so the transport owns no DTOs. */
export interface CachedHousehold {
  id: string;
  name: string;
  role: HouseholdRole;
}

export class HouseholdManager {
  private readonly sessions = new Map<string, HouseholdSession>();
  private readonly opening = new Map<string, Promise<HouseholdSession>>();
  private readonly statusListeners = new Set<StatusListener>();
  private readonly serverErrorListeners = new Set<ServerErrorListener>();
  private readonly syncedListeners = new Set<SyncedListener>();
  private syncedHouseholds: Set<string> | null = null;
  /** Server time from the most recent connection, used to clamp skewed device clocks. */
  private lastServerTime: number | null = null;
  private online = true;

  constructor(private readonly options: HouseholdManagerOptions) {}

  /** Connection-state changes for every open household, for offline indicators. */
  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  private emitStatus(householdId: string, status: ConnectionStatus): void {
    this.options.onStatusChange?.(householdId, status);
    for (const listener of this.statusListeners) listener(householdId, status);
  }

  /** Server rejections for every open household, for the quota banner and lock screen. */
  onServerError(listener: ServerErrorListener): () => void {
    this.serverErrorListeners.add(listener);
    return () => this.serverErrorListeners.delete(listener);
  }

  private emitServerError(householdId: string, error: SyncErrorPayload): void {
    this.options.onServerError?.(householdId, error);
    for (const listener of this.serverErrorListeners) listener(householdId, error);
  }

  /**
   * Whether this device has ever received the whole household from the server.
   *
   * Status alone cannot answer this: 'synced' is lost on the next reload, and a returning
   * device that opens offline still has every recipe on disk.
   */
  hasSynced(householdId: string): boolean {
    return this.loadSyncedHouseholds().has(householdId);
  }

  /** Fires the first time a household finishes syncing on this device. */
  onFirstSync(listener: SyncedListener): () => void {
    this.syncedListeners.add(listener);
    return () => this.syncedListeners.delete(listener);
  }

  private markSynced(householdId: string): void {
    const synced = this.loadSyncedHouseholds();
    if (synced.has(householdId)) return;
    synced.add(householdId);
    this.options.storage.setItem(SYNCED_HOUSEHOLDS_KEY, JSON.stringify([...synced]));
    for (const listener of this.syncedListeners) listener(householdId);
  }

  private loadSyncedHouseholds(): Set<string> {
    if (this.syncedHouseholds != null) return this.syncedHouseholds;
    let ids: string[] = [];
    try {
      const raw = this.options.storage.getItem(SYNCED_HOUSEHOLDS_KEY) ?? '[]';
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) ids = parsed.filter((id): id is string => typeof id === 'string');
    } catch {
      // A corrupt entry only costs one "syncing" empty state; the next sync rewrites it.
    }
    this.syncedHouseholds = new Set(ids);
    return this.syncedHouseholds;
  }

  /**
   * Follows the platform's own view of connectivity.
   *
   * A socket on a device that just lost signal is often not closed by anything: no FIN
   * arrives, so it sits open until a write times out minutes later. Taking the platform's
   * word for it means the indicator turns honest immediately and reconnection starts the
   * moment the network is back rather than at the end of a backoff window.
   */
  setNetworkOnline(online: boolean): void {
    if (this.online === online) return;
    this.online = online;
    for (const session of this.sessions.values()) {
      if (online) session.connection.connect();
      else session.connection.disconnect();
    }
  }

  isNetworkOnline(): boolean {
    return this.online;
  }

  getActiveHouseholdId(): string | null {
    return this.options.storage.getItem(ACTIVE_HOUSEHOLD_KEY);
  }

  setActiveHouseholdId(householdId: string): void {
    this.options.storage.setItem(ACTIVE_HOUSEHOLD_KEY, householdId);
  }

  /**
   * The household list comes from the server, but a device that cannot reach the server has
   * not left its households. Keeping the last answer is what lets the switcher — and with it
   * the whole app — come up offline.
   */
  cacheHouseholds(households: CachedHousehold[]): void {
    this.options.storage.setItem(HOUSEHOLD_LIST_KEY, JSON.stringify(households));
  }

  cachedHouseholds(): CachedHousehold[] | null {
    const raw = this.options.storage.getItem(HOUSEHOLD_LIST_KEY);
    if (raw == null) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as CachedHousehold[]) : null;
    } catch {
      return null;
    }
  }

  getSession(householdId: string): HouseholdSession | undefined {
    return this.sessions.get(householdId);
  }

  /**
   * Opens a household: local state first, network second.
   *
   * The store is usable the moment persisted updates have been applied. Connecting is
   * deliberately not awaited, so switching households on a plane is instant rather than
   * hanging on a socket that will never open.
   */
  async open(householdId: string, role: HouseholdRole, connect = true): Promise<HouseholdSession> {
    const existing = this.sessions.get(householdId);
    if (existing != null) {
      existing.lastUsedAt = Date.now();
      if (connect && this.online) existing.connection.connect();
      return existing;
    }

    const inFlight = this.opening.get(householdId);
    if (inFlight != null) return inFlight;

    const pending = this.createSession(householdId, role, connect);
    this.opening.set(householdId, pending);
    try {
      const session = await pending;
      this.sessions.set(householdId, session);
      this.evictIfNeeded(householdId);
      return session;
    } finally {
      this.opening.delete(householdId);
    }
  }

  private async createSession(
    householdId: string,
    role: HouseholdRole,
    connect: boolean,
  ): Promise<HouseholdSession> {
    const documentId = `household:${householdId}`;
    const handle = createDocument(documentId);

    // Persistence is attached before the store exists so the update log captures the very
    // first transaction. Starting the log later leaves a causal gap Yjs cannot apply.
    const persistence = attachPersistence(handle, this.options.createUpdateLog(documentId), {
      onError: this.options.onPersistenceError,
    });
    await persistence.whenReady;

    const context: StoreContext = {
      householdId,
      userId: this.options.userId,
      role,
      now: () => Date.now(),
      newId: this.options.newId ?? randomId,
      lastKnownServerTime: () => this.lastServerTime,
    };

    const { store } = createYjsStore({
      handle,
      context,
      revisions: this.options.revisions?.(householdId),
    });

    const connection = connectSync({
      url:
        this.options.syncUrl?.(householdId) ??
        `${this.options.apiBaseUrl.replace(/^http/, 'ws')}/sync/${householdId}`,
      handle,
      webSocketImpl: this.options.webSocketImpl,
      onStatus: (status) => this.emitStatus(householdId, status),
      onServerError: (error) => this.emitServerError(householdId, error),
      onSynced: () => this.markSynced(householdId),
      onInfo: (info) => {
        this.lastServerTime = info.serverTime;
      },
    });

    if (connect && this.online) connection.connect();

    return { householdId, role, handle, store, connection, persistence, lastUsedAt: Date.now() };
  }

  /** Disconnects the least-recently-used household, keeping its local data on disk. */
  private evictIfNeeded(keepHouseholdId: string): void {
    if (this.sessions.size <= MAX_CONCURRENT_HOUSEHOLDS) return;
    const active = this.getActiveHouseholdId();
    const candidates = [...this.sessions.values()]
      .filter((s) => s.householdId !== keepHouseholdId && s.householdId !== active)
      .sort((a, b) => a.lastUsedAt - b.lastUsedAt);

    while (this.sessions.size > MAX_CONCURRENT_HOUSEHOLDS && candidates.length > 0) {
      const victim = candidates.shift()!;
      void this.close(victim.householdId);
    }
  }

  async close(householdId: string): Promise<void> {
    const session = this.sessions.get(householdId);
    if (session == null) return;
    this.sessions.delete(householdId);
    session.connection.destroy();
    session.store.destroy();
    await session.persistence.destroy();
    session.handle.destroy();
  }

  async closeAll(): Promise<void> {
    await Promise.all([...this.sessions.keys()].map((id) => this.close(id)));
  }
}
